import React, { useState, useEffect } from 'react';
import axios from 'axios';
import GlassCard from './GlassCard';
import GlassButton from './GlassButton';
import { motion, AnimatePresence } from 'framer-motion';

interface ConnectedBroker {
  broker_name: string;
  environment: string;
  is_active: boolean;
  last_sync: string | null;
}

const BrokerBridge: React.FC = () => {
  const [brokers, setBrokers] = useState<ConnectedBroker[]>([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  
  // Form State
  const [formData, setFormData] = useState({
    broker_name: 'OANDA',
    account_id: '',
    api_key: '',
    environment: 'sandbox'
  });

  const apiUrl = import.meta.env.VITE_API_URL || import.meta.env.NEXT_PUBLIC_API_URL || 'http://localhost:8000';

  const fetchBrokers = async () => {
    try {
      const response = await axios.get(`${apiUrl}/api/v1/users/broker-accounts`);
      setBrokers(response.data);
    } catch (err) {
      console.error('Failed to fetch broker accounts', err);
    }
  };

  useEffect(() => {
    fetchBrokers();
  }, []);

  const handleSync = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      await axios.post(`${apiUrl}/api/v1/users/broker-sync`, formData);
      setIsModalOpen(false);
      fetchBrokers();
      // Reset form
      setFormData({
        broker_name: 'OANDA',
        account_id: '',
        api_key: '',
        environment: 'sandbox'
      });
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Broker synchronization failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h3 className="text-xs font-black uppercase tracking-[0.2em] text-text-tertiary">Institutional Broker Bridges</h3>
        <GlassButton 
          variant="primary" 
          size="sm" 
          onClick={() => setIsModalOpen(true)}
          className="rounded-xl px-6 h-10 uppercase tracking-widest font-black text-[9px]"
        >
          Add Connection
        </GlassButton>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {brokers.length > 0 ? (
          brokers.map((broker) => (
            <GlassCard key={broker.broker_name} variant="elevated" className="p-6 border-white/5 group hover:border-brand-teal/30 transition-all">
              <div className="flex justify-between items-start mb-4">
                <div className="space-y-1">
                  <div className="text-[10px] font-black text-brand-teal-light uppercase tracking-widest">{broker.broker_name}</div>
                  <div className="text-xs font-bold text-text-primary uppercase tracking-tight">{broker.environment} Environment</div>
                </div>
                <div className={`px-2 py-0.5 rounded text-[8px] font-black uppercase tracking-tighter ${
                  broker.is_active ? 'bg-brand-teal/10 text-brand-teal border border-brand-teal/20' : 'bg-red-500/10 text-red-400'
                }`}>
                  {broker.is_active ? 'Synced' : 'Offline'}
                </div>
              </div>
              <div className="text-[9px] text-text-tertiary uppercase tracking-widest">Last Neural Sync: {broker.last_sync ? new Date(broker.last_sync).toLocaleString() : 'Never'}</div>
            </GlassCard>
          ))
        ) : (
          <div className="col-span-2 p-10 border border-dashed border-white/10 rounded-3xl text-center">
            <p className="text-xs font-bold text-text-tertiary uppercase tracking-widest opacity-50">No Active Broker Bridges Found</p>
          </div>
        )}
      </div>

      {/* Sync Modal */}
      <AnimatePresence>
        {isModalOpen && (
          <div className="fixed inset-0 z-[100] flex items-center justify-center p-6">
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setIsModalOpen(false)}
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            />
            <motion.div 
              initial={{ scale: 0.9, opacity: 0, y: 20 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.9, opacity: 0, y: 20 }}
              className="relative w-full max-w-lg"
            >
              <GlassCard variant="glow" className="p-8 border-brand-teal/20">
                <h3 className="text-xl font-black text-white uppercase tracking-tight mb-8">Establish Neural Bridge</h3>
                
                <form onSubmit={handleSync} className="space-y-6">
                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-text-tertiary uppercase tracking-widest ml-1">Select Brokerage</label>
                    <select 
                      value={formData.broker_name}
                      onChange={(e) => setFormData({...formData, broker_name: e.target.value})}
                      className="w-full bg-white/[0.03] border border-white/10 rounded-2xl px-6 py-4 text-sm font-bold text-text-primary focus:ring-2 focus:ring-brand-teal/30 outline-none transition-all"
                    >
                      <option value="OANDA">OANDA (Forex)</option>
                      <option value="ALPACA">ALPACA (Stocks)</option>
                      <option value="STRIKE">STRIKE (Bitcoin)</option>
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-text-tertiary uppercase tracking-widest ml-1">Account ID</label>
                       <input 
                         required
                         type="text"
                         value={formData.account_id}
                         onChange={(e) => setFormData({...formData, account_id: e.target.value})}
                         placeholder="e.g. 101-001-..."
                         className="w-full bg-white/[0.03] border border-white/10 rounded-2xl px-6 py-4 text-sm font-bold text-text-primary focus:ring-2 focus:ring-brand-teal/30 underline-none transition-all placeholder:opacity-20"
                       />
                    </div>
                    <div className="space-y-2">
                       <label className="text-[10px] font-black text-text-tertiary uppercase tracking-widest ml-1">Environment</label>
                       <select 
                         value={formData.environment}
                         onChange={(e) => setFormData({...formData, environment: e.target.value})}
                         className="w-full bg-white/[0.03] border border-white/10 rounded-2xl px-6 py-4 text-sm font-bold text-text-primary focus:ring-2 focus:ring-brand-teal/30 outline-none transition-all"
                       >
                         <option value="sandbox">Sandbox / Paper</option>
                         <option value="production" disabled>Production (Restricted)</option>
                       </select>
                    </div>
                  </div>

                  <div className="space-y-2">
                    <label className="text-[10px] font-black text-text-tertiary uppercase tracking-widest ml-1">API Private Key</label>
                    <input 
                      required
                      type="password"
                      value={formData.api_key}
                      onChange={(e) => setFormData({...formData, api_key: e.target.value})}
                      placeholder="************************************"
                      className="w-full bg-white/[0.03] border border-white/10 rounded-2xl px-6 py-4 text-sm font-bold text-text-primary focus:ring-2 focus:ring-brand-teal/30 underline-none transition-all placeholder:opacity-20"
                    />
                  </div>

                  {error && (
                    <div className="p-4 bg-red-500/10 border border-red-500/20 rounded-2xl text-[10px] font-black text-red-400 uppercase tracking-widest">
                      {error}
                    </div>
                  )}

                  <div className="flex gap-4 pt-4">
                    <button 
                      type="button"
                      onClick={() => setIsModalOpen(false)}
                      className="flex-1 px-6 py-4 bg-white/5 hover:bg-white/10 border border-white/10 rounded-2xl text-[10px] font-black text-white uppercase tracking-widest transition-all"
                    >
                      Abort
                    </button>
                    <button 
                      type="submit"
                      disabled={loading}
                      className={`flex-1 px-6 py-4 bg-brand-teal/20 hover:bg-brand-teal/30 border border-brand-teal/20 rounded-2xl text-[10px] font-black text-brand-teal-light uppercase tracking-widest transition-all ${loading ? 'opacity-50 animate-pulse' : ''}`}
                    >
                      {loading ? 'Establish Link...' : 'Sync Bridge'}
                    </button>
                  </div>
                </form>
              </GlassCard>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default BrokerBridge;
